import {
  Button,
  Icon,
  Modal,
  PageHeader,
  Switch,
  Table,
  TableColumn,
} from '@components';
import UserAPICaller, { UserFilters } from '@services/api/user';
import { User } from 'interfaces/User';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import UserForm from './UserForm/UserForm';

import { useAuthContext } from '@contexts/AuthProvider';

export default function UserPage() {
  const { user } = useAuthContext();
  const [tableData, setTableData] = useState<{
    total: number;
    result: Array<User>;
  }>({
    total: 0,
    result: [],
  });
  const [filters, setFilters] = useState<UserFilters>({ order: 'name' });

  const [isLoading, setIsLoading] = useState(false);
  const [isEditModalOpen, toggleEditModal] = useState(false);
  const [editId, setEditId] = useState('');

  const { setError, setValue, getValues } = useForm();

  useEffect(() => {
    loadTableData(filters);
  }, [filters]);

  const loadTableData = (filters: UserFilters) => {
    setIsLoading(true);
    UserAPICaller.fetchUsers(filters)
      .then((values) => {
        setTableData(values);
      })
      .finally(() => setIsLoading(false));
  };

  const setUserValues = async (
    userId: string,
    active?: boolean,
    admin?: boolean
  ) => {
    await UserAPICaller.fetchUser(userId).then((user) => {
      setValue('id', user.id);
      setValue('name', user.name);
      setValue('email', user.email);
      setValue('active', active !== undefined ? active : user.active);
      setValue('admin', admin !== undefined ? admin : user.admin);
    });
  };

  const openEditModal = async (editId: string) => {
    setEditId(editId);
    toggleEditModal(true);
  };

  const activeOrInactiveUser = async (userId: string, active: boolean) => {
    setUserValues(userId, active).then(() => {
      const user = getValues() as User;

      UserAPICaller.createOrUpdate(
        {
          ...user,
          active,
        },
        setError
      ).then(() => loadTableData(filters));
    });
  };

  return (
    <>
      <div className="p-s-300">
        <PageHeader title="Usuários" />

        <div className="mt-s-200">
          <Table
            data={tableData.result}
            totalRecords={tableData.total}
            showColumnSelector={false}
            withPagination
            withSearchInput
            filters={filters}
            setFilters={setFilters}
            isLoading={isLoading}
            additionalFilters={
              <div className="d-flex justify-end">
                <Button
                  className="ml-s-100"
                  suffixes={<Icon>add</Icon>}
                  design="filled"
                  onClick={() => {
                    setEditId('');
                    return toggleEditModal(true);
                  }}
                >
                  Novo usuário
                </Button>
              </div>
            }
          >
            <TableColumn fromKey="name" header="Usuário" />
            <TableColumn fromKey="email" header="E-mail" />
            <TableColumn
              fromKey="admin"
              header="Função"
              disableSorting
              render={(admin) => {
                return admin ? 'Admin do sistema' : '-';
              }}
            />
            <TableColumn
              fromKey="id"
              header="Status"
              disableSorting
              render={(id, row) => {
                return (
                  <Switch
                    label=""
                    caption={row.active ? 'Ativo' : 'Inativo'}
                    defaultChecked={row.active}
                    onChange={() => activeOrInactiveUser(id, !row.active)}
                    disabled={id === user?.id}
                  />
                );
              }}
            />

            <TableColumn
              width="5%"
              fromKey="id"
              header="Editar"
              disableSorting
              render={(id) => {
                return (
                  <Button
                    design="transparent"
                    prefixes={<Icon>edit</Icon>}
                    onClick={() => {
                      openEditModal(id);
                    }}
                  />
                );
              }}
            />
          </Table>
        </div>
      </div>

      <Modal
        isOpen={isEditModalOpen}
        onClickAway={() => {
          setEditId('');
          toggleEditModal(false);
        }}
        size="extra-small"
        title={editId === '' ? 'Cadastrar usuário' : 'Editar usuário'}
      >
        <UserForm
          editId={editId}
          onSave={() => loadTableData(filters)}
          closeModal={() => {
            setEditId('');
            toggleEditModal(false);
          }}
          isMe={editId === user?.id}
        />
      </Modal>
    </>
  );
}
